import React, { useEffect } from "react";
import BaseList from "../../../components/partials/common/base-list";
import { useTranslation } from "react-i18next";
import { getPathParam, getSelectParam } from "../../../utils/converter";
import { SUB_CLASSIFIERS, SUB_CLASSIFIER, CLASSIFIER } from "../../../api/endpoints";
import { useDispatch, useSelector } from "react-redux";
import ActionsMenu from "../../../components/common/table/actions-menu";
import CardDetail from "../../../components/common/list/card-detail";
import TableDetail from "../../../components/common/list/table-detail";
import { useForm } from "react-hook-form";
import InputText from "../../../components/common/form/input-text";
import { Warning } from "../../../components/common/alert/banner";
import InputSubmit from "../../../components/common/form/input-submit";
import { fetchCountries } from "../../../reducers/configReducer";
import CatalogIcon from "../../../resources/icons/CatalogIcon";
import { SUB_CLASSIFIER_IMPORT } from "../../../constants/import";
import exampleFile from "../../../resources/import/sub-exemple-file.csv";
import { useOutletContext, useParams } from "react-router-dom";
import BasePermissionWrapper, { hasPermissionFor } from "../../../components/partials/restricted/base-permission-wrapper";
import { getPermission, permissionGroup, permissionType } from "../../../constants/permissions";



const SubClassifier = () => {
    const { t } = useTranslation();
    const { loggedCompany } = useSelector((state) => state.user);
    let { id } = useParams();

    const { onSetTitle } = useOutletContext();

    const { permissions } = useSelector((s) => s.userCompany);

    const canCreate = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.CREATE);
    const canEdit = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.EDIT);
    const canView = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.VIEW);


    return (
        <BasePermissionWrapper requiredPermissions={[getPermission(permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.LIST)]} >
            {
                loggedCompany && (
                    <BaseList
                        columns={[
                            t("app.sub_classifiers.sub_classifier_name"),
                            t("app.sub_classifiers.sub_classifier_code"),
                            "",
                        ]}
                        endpoint={getPathParam([CLASSIFIER, id, SUB_CLASSIFIERS])}
                        storeEndpoint={getPathParam([CLASSIFIER, id, SUB_CLASSIFIER])}
                        updateEndpoint={getPathParam([CLASSIFIER, id, SUB_CLASSIFIER, ':id'])}
                        baseResult={{ total_rows: 0, sub_classifiers: [] }}
                        resultLabel={'sub_classifiers'}
                        resultTitle={'classifier_name'}
                        onSetTitle={(r) => { onSetTitle(r) }}
                        title={t("app.sub_classifiers.sub_classifiers")}
                        showHeader={true}
                        mobRow={MobRow}
                        deskRow={DeskRow}
                        addForm={AddForm}
                        icon={CatalogIcon}
                        addFormLabel={t("app.sub_classifiers.add_sub_classifier")}
                        btnAddLabel={canCreate ? t("app.sub_classifiers.add_sub_classifier") : false}
                        addPermission={[getPermission(permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.CREATE)]}
                        editForm={EditForm}
                        editTitle={t("app.sub_classifiers.sub_classifiers")}
                        isActiveEdit={canView || canEdit}
                        isActiveImport={canEdit}
                        btnImportLabel={t("app.catalog.import")}
                        importType={canCreate ? 'product' : false}
                        uploadType={'sub_classifier'}
                        CSVHeader={SUB_CLASSIFIER_IMPORT}
                        exampleFile={exampleFile}
                    />
                )
            }

        </BasePermissionWrapper>
    );
};


const MobRow = ({ element, extras }) => {
    const { t } = useTranslation();
    let { id } = useParams();

    const removeDetail = {
        title: t("app.sub_classifiers.remove_title"),
        message: t("app.sub_classifiers.remove_description"),
        endpoint: getPathParam([CLASSIFIER, id, SUB_CLASSIFIER, element.id])
    };

    const { permissions } = useSelector((s) => s.userCompany);

    const canEdit = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.EDIT);
    const canDelete = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.DELETE);
    const canView = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.VIEW);


    return (
        <li key={element.id} className="flex items-center gap-4 p-4">
            <div className="flex-grow">
                <div className="pb-4 items-center flex justify-between">
                    {element.name}
                    {
                        (canView || canEdit || canDelete) && (
                            <ActionsMenu>
                                {
                                    (canView || canEdit) && (
                                        <button className={'text-left'} onClick={() => {
                                            extras.onEdit(true, element);
                                        }}
                                        >
                                            {t("app.common.edit")}
                                        </button>
                                    )
                                }
                                {
                                    canDelete && (
                                        <button className={'text-left'} onClick={() => {
                                            extras.onRemove(true, removeDetail);
                                        }}
                                        >
                                            {t("app.common.remove")}
                                        </button>
                                    )
                                }
                            </ActionsMenu>
                        )
                    }
                </div>
                <div className="flex flex-col">
                    <CardDetail title={t("app.sub_classifiers.sub_classifier_name")}>
                        {element.name}
                    </CardDetail>

                    <CardDetail title={t("app.sub_classifiers.sub_classiffer_code")}>
                        {element?.reference ?? '-'}
                    </CardDetail>
                    <CardDetail title={t("app.sub_classifiers.sub_classiffer_description")}>
                        {element?.description ?? '-'}
                    </CardDetail>

                </div>
            </div>
        </li>
    );
};

const DeskRow = ({ element, extras }) => {
    const { t } = useTranslation();
    let { id } = useParams();

    const removeDetail = {
        title: t("app.sub_classifiers.remove_title"),
        message: t("app.sub_classifiers.remove_description"),
        endpoint: getPathParam([CLASSIFIER, id, SUB_CLASSIFIER, element.id])
    };

    const { permissions } = useSelector((s) => s.userCompany);

    const canEdit = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.EDIT);
    const canDelete = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.DELETE);
    const canView = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.VIEW);


    return (
        <tr key={element.id}>
            <TableDetail extraClass="max-w-sm">
                <div className="flex items-center space-x-3 lg:pl-2">
                    <div className="truncate hover:text-gray-600">
                        {element.name}
                    </div>
                </div>
            </TableDetail>

            <TableDetail>
                {element?.reference ?? '-'}
            </TableDetail>

            <TableDetail>
                {
                    (canView || canEdit || canDelete) && (
                        <ActionsMenu>
                            {
                                (canView || canEdit) && (
                                    <button className={'text-left'} onClick={() => {
                                        extras.onEdit(true, element);
                                    }}
                                    >
                                        {t("app.common.edit")}
                                    </button>
                                )
                            }
                            {
                                canDelete && (
                                    <button className={'text-left'} onClick={() => {
                                        extras.onRemove(true, removeDetail);
                                    }}
                                    >
                                        {t("app.common.remove")}
                                    </button>
                                )
                            }
                        </ActionsMenu>
                    )
                }
            </TableDetail>
        </tr>
    );
};

const AddForm = ({ exception, isLoading, onSubmitData }) => {

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();


    const dispatch = useDispatch();

    const { t } = useTranslation();

    const onSubmit = async (data) => {
        onSubmitData(data);
    };

    useEffect(() => {
        dispatch(fetchCountries());
    }, [dispatch]);




    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-y-3 gap-x-4 sm:grid-cols-1">
                    <InputText
                        id={"name"}
                        type={"text"}
                        errors={errors.name}
                        input={{ ...register("name", { required: true }) }}
                        label={t("app.sub_classifiers.sub_classifier_name")}
                    />


                    <InputText
                        id={"reference"}
                        type={"text"}
                        errors={errors.reference}
                        input={{ ...register("reference", { required: true }) }}
                        label={t("app.sub_classifiers.sub_classifier_code")}
                    />

                    <InputText
                        id={"description"}
                        type={"text"}
                        errors={errors.description}
                        input={{ ...register("description", { required: true }) }}
                        label={t("app.sub_classifiers.sub_classifier_description")}
                    />

                </div>
            </div>

            {/* Warning */}
            {exception && (
                <Warning message={exception} />
            )}

            <div className="flex before:flex-1 items-center justify-between mt-6">
                <InputSubmit
                    isLoading={isLoading}
                    label={t("app.sub_classifiers.add_sub_classifier")}
                />
            </div>
        </form>
    );
}

const EditForm = ({ data, exception, onSubmitData, isEditLoading }) => {

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm();

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { permissions } = useSelector((s) => s.userCompany);

    const canEdit = hasPermissionFor(permissions, permissionGroup.PRODUCT_SETTING_CLASSIFIER, permissionType.EDIT);

    useEffect(() => {
        if (data !== null) {
            for (const k in data) {
                if (typeof data[k] === "object") {
                    setValue(k, getSelectParam(data[k], "name"));
                } else {
                    setValue(k, data[k]);
                }
            }
        }
    }, [setValue, data]);

    const onSubmit = async (data) => {
        onSubmitData(data);
    };


    useEffect(() => {
        dispatch(fetchCountries());
    }, [dispatch]);

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div>

                    <h3 className="mt-4 text-md font-medium text-gray-900">
                        {t("app.sub_classifiers.sub_classifier")}
                    </h3>

                    <div className="space-y-4 pt-4 pb-5">

                        <InputText
                            id={"name"}
                            type={"text"}
                            errors={errors.name}
                            input={{ ...register("name", { required: true }) }}
                            label={t("app.sub_classifiers.sub_classifier_name")}
                            isDisabled={!canEdit}
                        />


                        <InputText
                            id={"reference"}
                            type={"text"}
                            errors={errors.reference}
                            input={{ ...register("reference", { required: true }) }}
                            label={t("app.sub_classifiers.sub_classifier_code")}
                            isDisabled={!canEdit}
                        />

                    </div>
                </div>

                {/* Warning */}
                {exception && (
                    <Warning message={exception} />
                )}

                {
                    canEdit && (
                        <div className="mt-3">
                            <InputSubmit
                                isLoading={isEditLoading}
                                isFullWith={true}
                                label={t("app.form.save")}
                            />
                        </div>
                    )
                }

            </form>

        </>
    );
}

export default SubClassifier;
